<template>
  <ContentSection :spacing="index === 0 ? 'inline_text_top' : ''">
    <Container indent="wide">
      <div
        class="xl:mb-x-12 mb-10 flex flex-wrap items-end gap-4 sm:gap-6 2xl:justify-between"
      >
        <!-- TAB BUTTONS -->
        <div class="flex sm:mt-6">
          <button
            class="gradient-transition-effect group relative z-10 flex h-13 w-fit items-center overflow-hidden rounded-tl-md bg-gradient-to-tr px-5 py-3 text-base font-medium after:bg-gradient-to-tr sm:h-16 sm:px-6"
            :class="
              !showFacebookFeed
                ? 'from-blue-800 to-blue-500 text-white after:from-blue-dark after:to-blue-500'
                : 'from-blue-100/50 to-blue-100/20 text-blue-800 after:from-blue-100/60 after:to-blue-100/30'
            "
            @click="toggleFacebookFeed(false)"
          >
            Schweiz
          </button>
          <button
            class="gradient-transition-effect group relative z-10 flex h-13 w-fit items-center overflow-hidden rounded-tr-md bg-gradient-to-tr px-5 py-3 text-base font-medium after:bg-gradient-to-tr sm:h-16 sm:px-6"
            :class="
              showFacebookFeed
                ? 'from-blue-800 to-blue-500 text-white after:from-blue-dark after:to-blue-500'
                : 'from-blue-100/50 to-blue-100/20 text-blue-800 after:from-blue-100/60 after:to-blue-100/30'
            "
            @click="toggleFacebookFeed(true)"
          >
            SI International
          </button>
        </div>

        <!-- FILTER -->
        <div v-show="!showFacebookFeed">
          <Filter
            v-if="Array.isArray(terms) && terms.length > 0"
            :terms="terms"
            :filtered="filter.newsType"
            :button-all-text="$t('all')"
            @filter="filter.newsType = $event"
          >
            {{ $t('filter.filter_for_type') }}
          </Filter>
        </div>
        <!-- TEXT SEARCH -->
        <SearchInput
          v-show="!showFacebookFeed"
          class="hidden w-full min-w-[13rem] sm:block lg:max-w-[20rem]"
          :model-value="filter.text"
          @update:model-value="filter.text = $event"
        />
        <!-- TEXT SEARCH MOBILE -->
        <div
          v-show="!showFacebookFeed"
          class="relative min-h-[1.625rem] w-full min-w-[13rem] sm:hidden lg:max-w-[20rem]"
        >
          <TransitionExpand
            class="w-full overflow-hidden transition-all delay-200 duration-300 ease-in-out"
            :open="isShowingMore"
          >
            <SearchInput
              class="w-full min-w-[13rem] lg:max-w-[20rem]"
              small
              :model-value="filter.text"
              @update:model-value="filter.text = $event"
            />
          </TransitionExpand>
          <transition name="fade">
            <button
              v-show="!isShowingMore"
              class="absolute left-0 top-0 flex items-center gap-2 text-base text-blue-800"
              @click="isShowingMore = true"
            >
              <PlusIcon class="w-[0.625rem] shrink-0" />
              <span>{{ $t('show_more') }}</span>
            </button>
          </transition>
        </div>
      </div>
    </Container>

    <!-- NEWS -->
    <client-only>
      <Container v-show="!showFacebookFeed">
      <ul class="grid w-full grid-cols-2 gap-4 sm:gap-6 xl:grid-cols-4">
        <li
          v-for="article in newsComposed"
          :key="article.id"
          :class="article.colSpan"
        >
          <NewsCard :data="article" />
        </li>
      </ul>

      <!-- LOAD MORE -->
      <div
        v-show="hasMorePages || news.length > newsComposed.length"
        class="mt-14 flex w-full justify-center"
      >
        <AppButton @click="filter.limit += initialLimit">
          {{ $t('filter.load_more') }}
        </AppButton>
      </div>

      <!-- NO ENTRIES FOUND -->
      <transition name="fade">
        <div
          v-show="newsComposed.length === 0"
          class="my-10 flex w-full items-center justify-center gap-4 text-blue-dark-800 sm:my-20"
        >
          <MagnifyingLensIcon class="w-[1.125rem] shrink-0" />
          <p class="text-md text-blue-dark-800">
            {{ $t('filter.no_entries_found') }}
          </p>
        </div>
      </transition>
    </Container>
    </client-only>
    <!-- FACEBOOK -->
    <NewshubFacebook v-show="showFacebookFeed" :id="data.id" />
  </ContentSection>
</template>

<script setup>
const props = defineProps({
  data: { type: Object, default: null },
  index: { type: Number, default: null },
})

const { locale } = useI18n()
const route = useRoute()
const nowInZurich = useNowInZurich()
const params = useUrlSearchParams('history')
const news = ref(null)
const terms = ref(null)
const hasMorePages = ref(null)
const initialLimit = ref(16)
const isShowingMore = ref(false)

const showFacebookFeed = ref(false)

provide(
  'news',
  computed(() => newsComposed.value)
)

onMounted(async () => {
  await nextTick()
  showFacebookFeed.value =
    route.query[`show-facebook-feed-${props.data.id}`] === 'true' ? true : false
})

// Populate the filter
let routeQueryFilter = route.query[`newshub-${props.data.id}`]
if (routeQueryFilter) {
  routeQueryFilter = JSON.parse(routeQueryFilter)
}

// Could be that the route has ha filter query when routed from a news pages TypeTag
const initialFilterNewsType = route.query.newsType || null

const filter = ref(
  routeQueryFilter || {
    newsType: initialFilterNewsType,
    text: null,
    limit: initialLimit.value,
  }
)

// WATCHERS
watch(
  filter,
  (newValue) => {
    params[`newshub-${props.data.id}`] = JSON.stringify(newValue)
    fetchNews()
  },
  { deep: true }
)

// COMPUTED
const newsComposed = computed(() => {
  if (!Array.isArray(news.value) || !news.value.length) {
    return []
  }
  const newsAmount = news.value.length
  let newsComposed = []

  // Loop through the news and determine the colSpan
  for (let index = 0; index < newsAmount; index++) {
    const article = JSON.parse(JSON.stringify(news.value[index]))
    const articleBefore = index > 0 ? news.value[index - 1] : null
    const articleAfter = index < newsAmount - 1 ? news.value[index + 1] : null
    const isVideo = !!article.image_or_video[0]?.video
    const isImage = !isVideo

    // If the article is a video = col-span-2
    if (isVideo) {
      article.colSpan = 'col-span-2'

      if (index % 3 === 0 && articleBefore?.image_or_video[0]?.image) {
        newsComposed[newsComposed.length - 1].colSpan = 'col-span-2'
      }

      newsComposed.push(article)
      continue
    }

    if (isImage) {
      if (
        articleBefore?.image_or_video[0]?.video &&
        articleAfter?.image_or_video[0]?.video
      ) {
        article.colSpan = 'col-span-2'
      } else if (index % 2 === 0 && articleAfter?.image_or_video[0]?.video) {
        article.colSpan = 'col-span-2 xl:col-span-1'
      } else if (!articleAfter && index % 3 === 0) {
        article.colSpan = 'col-span-2'
      } else {
        article.colSpan = 'col-span-2 md:col-span-1'
      }
      newsComposed.push(article)
      continue
    }
  }

  return newsComposed
})

// FUNCTIONS
function toggleFacebookFeed(value) {
  showFacebookFeed.value = value
  params[`show-facebook-feed-${props.data.id}`] = value
}
async function fetchNews() {
  let payload = await useAsyncGql({
    operation: 'News',
    variables: {
      sort: 'date desc',
      limit: filter.value.limit,
      filter: {
        site: locale.value,
        news_type: { contains: filter.value.newsType },
        title: { contains: filter.value.text },
        date: { lte: nowInZurich },
      },
    },
  })

  payload = payload.data.value?.entries

  hasMorePages.value = payload.has_more_pages

  if (!payload) {
    console.error('News could not be fetched')
    return
  }

  news.value = payload.data
}

// INITIAL
let payload = await useAsyncGql({
  operation: 'NewsAndTerms',
  variables: {
    sort: 'date desc',
    limit: filter.value.limit,
    filter: {
      site: locale.value,
      news_type: { contains: filter.value.newsType },
      title: { contains: filter.value.text },
      date: { lte: nowInZurich },
    },
    filterLocale: {
      site: locale.value,
      date: { lte: nowInZurich },
    },
  },
})

payload = payload.data?.value

if (!payload) {
  console.error('News could not be fetched')
}

terms.value = payload.news_types?.data

news.value = payload.entries.data

hasMorePages.value = payload.entries.has_more_pages
</script>
